import { useState } from 'react'
import {
  ETICHETE_STARE,
  ETICHETE_TIP,
  STARI_PE_TIP,
  ETICHETE_SURSA,
  STARE_INITIALA,
  SURSE_CONTAINER,
  TIPURI_CAMPANIE,
  TIP_EVENIMENT,
  type TipCampanie,
  statusCanonic,
} from '@b4l/shared'
import { toast } from 'sonner'
import { Field } from '../../ui/Field'
import { Button } from '../../ui/Button'
import { Modal } from '../../ui/Modal'
import type { Campanie, UtilizatorScurt } from './comun'
import { SelectorParteneri, type PartenerAles } from './SelectorParteneri'

/** Containere create de sistem, nu de om. Vezi filtrul din lista de tipuri. */
const GENERATE_DE_SISTEM: readonly string[] = ['program', 'expunere']

/**
 * Formularul unei activitati — creare si editare.
 *
 * Folosit din doua locuri: lista de activitati („Activitate noua") si fila Sumar a paginii unei
 * activitati („Editeaza"). De aceea sta aici, nu in pagina de lista.
 */

export interface PropsFormular {
  titlu: string
  etichetaSalvare: string
  initial?: Campanie
  utilizatori: UtilizatorScurt[]
  onClose: () => void
  onSubmit: (date: Record<string, unknown>) => void
  seSalveaza: boolean
}

export function FormularContainer({
  titlu,
  etichetaSalvare,
  initial,
  utilizatori,
  onClose,
  onSubmit,
  seSalveaza,
}: PropsFormular) {
  const eEditare = initial != null
  const [tip, setTip] = useState<TipCampanie>(initial?.tip ?? 'eveniment')
  const [v, setV] = useState({
    titlu: initial?.titlu ?? '',
    tema: initial?.tema ?? '',
    dataStart: initial?.dataStart?.slice(0, 10) ?? '',
    dataFinal: initial?.dataFinal?.slice(0, 10) ?? '',
    status: initial?.status ?? STARE_INITIALA[initial?.tip ?? 'eveniment'],
    responsabilId: initial?.responsabilId ? String(initial.responsabilId) : '',
    bugetAlocat: initial?.bugetAlocat ?? '',
    observatii: initial?.observatii ?? '',
    tipEveniment: initial?.tipEveniment ?? '',
    oras: initial?.oras ?? '',
    locatie: initial?.locatie ?? '',
    organizator: initial?.organizator ?? '',
    sursa: initial?.sursa ?? '',
    contactNume: initial?.contactNume ?? '',
    contactEmail: initial?.contactEmail ?? '',
    contactTelefon: initial?.contactTelefon ?? '',
    canaleSociale: initial?.canaleSociale ?? '',
    ceAmAgreat: initial?.ceAmAgreat ?? '',
    taxaParticipare: initial?.taxaParticipare ?? '',
    taxaMoneda: initial?.taxaMoneda ?? 'RON',
    participantiEstimati: initial?.participantiEstimati ? String(initial.participantiEstimati) : '',
  })
  const [parteneri, setParteneri] = useState<PartenerAles[]>(() =>
    (initial?.parteneri ?? []).map((p) => ({
      partenerId: p.partener.id,
      nume: p.partener.denumire,
      cif: p.partener.codFiscal,
      rol: p.rol,
      nota: p.nota ?? '',
    })),
  )
  const [erori, setErori] = useState<Record<string, string>>({})
  const set = (k: keyof typeof v, val: string) => {
    setV((s) => ({ ...s, [k]: val }))
    // Mesajul de sub camp dispare de indata ce omul se atinge de el.
    setErori((e) => (e[k] ? { ...e, [k]: '' } : e))
  }

  const eEveniment = tip === 'eveniment'

  /*
    Aceleasi limite ca in schema Zod a API-ului. Verificarea de aici nu inlocuieste serverul —
    el ramane singura bariera reala — dar arata greseala PE CAMP, nu ca un refuz la salvare.
  */
  const LIMITE: Partial<Record<keyof typeof v, number>> = {
    titlu: 200,
    tema: 200,
    oras: 120,
    locatie: 200,
    organizator: 200,
    contactNume: 200,
    contactEmail: 200,
    contactTelefon: 40,
    canaleSociale: 2000,
    ceAmAgreat: 4000,
    observatii: 4000,
  }

  function verifica(): Record<string, string> {
    const e: Record<string, string> = {}
    if (!v.titlu.trim()) e.titlu = 'Titlul e obligatoriu.'
    if (!v.dataStart) e.dataStart = 'Data de început e obligatorie.'
    if (v.dataStart && v.dataFinal && v.dataFinal < v.dataStart) {
      e.dataFinal = 'Sfârșitul e înaintea începutului.'
    }
    for (const [camp, max] of Object.entries(LIMITE)) {
      const text = v[camp as keyof typeof v].trim()
      if (max && text.length > max) {
        e[camp] = `Prea lung: ${text.length} caractere, maximum ${max}.`
      }
    }
    // Partenerul se poate adăuga mai târziu: devine obligatoriu la prima factură legată
    // (Dorian, 26.10.2026). De atunci, lista nu mai poate fi golită — o oprește serverul.
    const email = v.contactEmail.trim()
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      e.contactEmail = 'Adresa nu pare validă. O singură adresă, fără nume în față.'
    }
    return e
  }

  function trimite(): void {
    const gresite = verifica()
    setErori(gresite)
    if (Object.keys(gresite).length > 0) {
      toast.error('Câmpurile marcate cu roșu trebuie corectate.')
      return
    }
    const d: Record<string, unknown> = {
      titlu: v.titlu.trim(),
      tema: v.tema.trim() || null,
      dataStart: v.dataStart,
      dataFinal: v.dataFinal || null,
      responsabilId: v.responsabilId ? Number(v.responsabilId) : null,
      bugetAlocat: v.bugetAlocat === '' ? null : Number(v.bugetAlocat),
      observatii: v.observatii.trim() || null,
    }
    if (!eEditare) d.tip = tip
    d.parteneri = parteneri.map((p) => ({
      partenerId: p.partenerId,
      rol: p.rol,
      nota: p.nota.trim() || null,
    }))
    // Sursa si contactul sunt ale oricarui tip: la o propunere fara partener, contactul spune de
    // la cine e. Trimise doar la eveniment, se pierdeau la barter, presa, sponsorizare.
    Object.assign(d, {
      sursa: v.sursa || null,
      contactNume: v.contactNume || null,
      contactEmail: v.contactEmail || null,
      contactTelefon: v.contactTelefon || null,
      canaleSociale: v.canaleSociale || null,
    })
    // La creare, starea aleasa pleaca si ea: serverul verifica sa apartina tipului.
    if (v.status) d.status = v.status
    if (eEveniment) {
      Object.assign(d, {
        tipEveniment: v.tipEveniment || null,
        oras: v.oras.trim() || null,
        locatie: v.locatie.trim() || null,
        organizator: v.organizator.trim() || null,
        ceAmAgreat: v.ceAmAgreat.trim() || null,
        taxaParticipare: v.taxaParticipare === '' ? null : Number(v.taxaParticipare),
        taxaMoneda: v.taxaParticipare === '' ? null : v.taxaMoneda,
        participantiEstimati: v.participantiEstimati === '' ? null : Number(v.participantiEstimati),
      })
    }
    onSubmit(d)
  }

  return (
    <Modal open title={titlu} onClose={onClose} size="lg" inchideLaClicInAfara={false}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Tip
            <select
              value={tip}
              disabled={eEditare}
              onChange={(e) => {
                const nou = e.target.value as TipCampanie
                setTip(nou)
                /*
                  Starea SARE pe implicita tipului nou. Fara asta, cine alege „Barter" dupa ce
                  formularul pornise pe „Eveniment" ar ramane cu „oportunitate", care nu exista la
                  barter — si ar afla abia din refuzul serverului, dupa ce a completat tot.
                */
                set('status', STARE_INITIALA[nou])
              }}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm disabled:bg-b4l-bg disabled:opacity-60"
            >
              {/*
                `program` si `expunere` nu se creeaza de mana: amandoua au container generat de
                sistem. La expuneri e o umbrela SINGURA, `EXP-PERMANENT` — daca ar ramane
                selectabila aici, oricine ar putea deschide a doua, iar regula „una singura" s-ar
                rupe tacut, adica s-ar descoperi peste luni, cand cifrele nu mai torn.
                La editare raman vizibile, ca sa nu dispara tipul unui container existent.
              */}
              {TIPURI_CAMPANIE.filter((t) => !GENERATE_DE_SISTEM.includes(t) || eEditare).map(
                (t) => (
                  <option key={t} value={t}>
                    {ETICHETE_TIP[t]}
                  </option>
                ),
              )}
            </select>
            {eEditare && (
              <span className="text-[11px] text-b4l-text-dim">
                Tipul nu se schimbă: codul îl conține.
              </span>
            )}
          </label>
          {/*
            Statusul se alege SI la creare — „un buton de status inainte de a salva containerul".
            Inainte se putea doar dupa, iar cine inregistra un eveniment deja confirmat trebuia sa-l
            salveze ca oportunitate si sa-l editeze imediat.

            Lista se ingusteaza dupa tip: serverul refuza oricum o stare care nu-i apartine.
          */}
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Stare
            <select
              value={v.status}
              onChange={(e) => set('status', e.target.value)}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            >
              {/*
                Starile finale (incheiata, anulata) nu se aleg de aici: inchiderea cere raport, iar
                anularea motiv — le face butonul „Închide” de pe pagina activitatii (15.10.2026).
                Starea curenta ramane in lista, ca selectul sa n-o piarda.
              */}
              {STARI_PE_TIP[tip]
                .filter(
                  (s) =>
                    s === initial?.status || !['completed', 'canceled'].includes(statusCanonic(s)),
                )
                .map((s) => (
                  <option key={s} value={s}>
                    {ETICHETE_STARE[s]}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <Field
          label="Titlu"
          value={v.titlu}
          onChange={(e) => set('titlu', e.target.value)}
          placeholder={eEveniment ? 'Sense4Fit' : 'Magneziu Q4'}
          error={erori.titlu}
        />
        <Field
          label="Temă"
          value={v.tema}
          onChange={(e) => set('tema', e.target.value)}
          error={erori.tema}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Început"
            type="date"
            value={v.dataStart}
            onChange={(e) => set('dataStart', e.target.value)}
            error={erori.dataStart}
          />
          <Field
            label="Sfârșit"
            type="date"
            value={v.dataFinal}
            onChange={(e) => set('dataFinal', e.target.value)}
            error={erori.dataFinal}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Responsabil
            <select
              value={v.responsabilId}
              onChange={(e) => set('responsabilId', e.target.value)}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              {utilizatori.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
          </label>
          <Field
            label="Buget alocat"
            type="number"
            value={v.bugetAlocat}
            onChange={(e) => set('bugetAlocat', e.target.value)}
            error={erori.bugetAlocat}
          />
        </div>

        <div className="grid gap-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-b4l-text">Parteneri</span>
            <span className="text-[11px] text-b4l-text-dim">
              la propunere, opțional — numele colaboratorului merge la „Persoană de contact”;
              obligatoriu înainte de prima factură
            </span>
          </div>
          <SelectorParteneri
            valoare={parteneri}
            onChange={(l) => {
              setParteneri(l)
              setErori((e) => (e.parteneri ? { ...e, parteneri: '' } : e))
            }}
            eroare={erori.parteneri}
          />
        </div>

        {/*
          Sursa si persoana de contact sunt ale ORICARUI container, nu doar ale evenimentului.
          Sursa statea in blocul „doar la eveniment"; la un barter cu cineva din social media,
          persoana de contact E intelegerea.
        */}
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Sursă
            <select
              value={v.sursa}
              onChange={(e) => set('sursa', e.target.value)}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              {SURSE_CONTAINER.map((x) => (
                <option key={x} value={x}>
                  {ETICHETE_SURSA[x]}
                </option>
              ))}
            </select>
          </label>
          <Field
            label="Persoană de contact"
            value={v.contactNume}
            onChange={(e) => set('contactNume', e.target.value)}
            placeholder="Numele celui cu care am vorbit"
            error={erori.contactNume}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Email contact"
            value={v.contactEmail}
            onChange={(e) => set('contactEmail', e.target.value)}
            error={erori.contactEmail}
          />
          <Field
            label="Telefon contact"
            value={v.contactTelefon}
            onChange={(e) => set('contactTelefon', e.target.value)}
            error={erori.contactTelefon}
          />
        </div>

        {/*
          Canalele apar la barter si la articolul de presa: acolo intelegerea se aplica pe un canal
          anume. La un eveniment sau la o chirie de spatiu n-ar avea ce cauta.
        */}
        {(tip === 'barter_social_media' || tip === 'articol_presa') && (
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Canale unde se aplică înțelegerea
            <textarea
              value={v.canaleSociale}
              onChange={(e) => set('canaleSociale', e.target.value)}
              rows={3}
              placeholder={'https://instagram.com/…\nhttps://tiktok.com/@…'}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            />
            {erori.canaleSociale && (
              <span className="text-xs text-b4l-red">{erori.canaleSociale}</span>
            )}
            <span className="text-[11px] text-b4l-text-dim">Câte un link pe rând.</span>
          </label>
        )}

        {eEveniment && (
          <div className="space-y-3 rounded border border-b4l-border bg-b4l-bg-soft p-3">
            <p className="text-[11px] uppercase tracking-wide text-b4l-text-dim">
              Doar la eveniment
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Tip eveniment
                <select
                  value={v.tipEveniment}
                  onChange={(e) => set('tipEveniment', e.target.value)}
                  className="rounded border border-b4l-border px-2 py-1.5 text-sm"
                >
                  <option value="">—</option>
                  {TIP_EVENIMENT.map((t) => (
                    <option key={t} value={t}>
                      {t === 'medical' ? 'Medical' : 'Non-medical'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Oraș"
                value={v.oras}
                onChange={(e) => set('oras', e.target.value)}
                error={erori.oras}
              />
              <Field
                label="Locație"
                value={v.locatie}
                onChange={(e) => set('locatie', e.target.value)}
                placeholder="Romexpo"
                error={erori.locatie}
              />
            </div>
            {/*
              Organizatorul e acum un partener (15.10.2026). Textul vechi ramane vizibil doar unde
              exista si nu s-a putut lega automat — ca omul sa stie pe cine sa caute mai sus.
            */}
            {initial?.organizator && !parteneri.some((p) => p.rol === 'organizator') && (
              <p className="rounded bg-b4l-amber-light px-2.5 py-1.5 text-xs text-b4l-amber-dark">
                Organizator scris înainte: <b>{initial.organizator}</b>. Caută-l mai sus, la
                Parteneri, cu rolul Organizator.
              </p>
            )}
            <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
              Ce am agreat să le dăm
              <textarea
                value={v.ceAmAgreat}
                onChange={(e) => set('ceAmAgreat', e.target.value)}
                rows={3}
                className="rounded border border-b4l-border px-2 py-1.5 text-sm"
                placeholder="Stand 3×2, 50 tricouri pentru voluntari, sampling la standul de recuperare"
              />
              {erori.ceAmAgreat && <span className="text-xs text-b4l-red">{erori.ceAmAgreat}</span>}
            </label>
            <div className="grid grid-cols-3 gap-3">
              <Field
                label="Taxă / pachet"
                type="number"
                value={v.taxaParticipare}
                onChange={(e) => set('taxaParticipare', e.target.value)}
              />
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Monedă
                <select
                  value={v.taxaMoneda}
                  onChange={(e) => set('taxaMoneda', e.target.value)}
                  className="rounded border border-b4l-border px-2 py-1.5 text-sm"
                >
                  <option value="RON">RON</option>
                  <option value="EUR">EUR</option>
                </select>
              </label>
              <Field
                label="Participanți estimați"
                type="number"
                value={v.participantiEstimati}
                onChange={(e) => set('participantiEstimati', e.target.value)}
              />
            </div>
          </div>
        )}

        <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
          Observații
          <textarea
            value={v.observatii}
            onChange={(e) => set('observatii', e.target.value)}
            rows={2}
            className="rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
          {erori.observatii && <span className="text-xs text-b4l-red">{erori.observatii}</span>}
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={trimite} loading={seSalveaza} disabled={!v.titlu.trim() || !v.dataStart}>
            {etichetaSalvare}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
